import { FontFamily } from "@redotech/redo-content-builder-common/element-schema";
import { PageOrientation } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { ObjectId } from "bson";
import { PageSize } from "../packing-slips/packing-slip-builder";
import {
  PoBlockType,
  PoFontWeight,
  PoLineItemColumnType,
  PoShipmentColumnType,
  type PoSection,
  type PoSectionType,
} from "./po-block-types";
import type { PoTemplate, PoTemplatePageLayout } from "./po-definition";

const defaultPadding = { top: 12, right: 12, bottom: 12, left: 12 };
const noPadding = { top: 0, right: 0, bottom: 0, left: 0 };

export const defaultFont = {
  fontFamily: FontFamily.ARIAL,
  fontSize: 12,
  textColor: "#000000",
  fontWeight: PoFontWeight.NORMAL,
};

const addressDefaults = {
  showHeader: false,
  header: "",
  showName: true,
  boldName: false,
  showStreet: true,
  showCity: true,
  showState: true,
  showPostalCode: true,
  showCountry: true,
  showPhone: false,
  showEmail: false,
};

// --- Company block defaults ---

export const companyNameDefault: PoSection.CompanyName = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.COMPANY_NAME,
  ...defaultFont,
  fontSize: 16,
  fontWeight: PoFontWeight.BOLD,
  sectionPadding: defaultPadding,
};

export const companyLogoDefault: PoSection.CompanyLogo = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.COMPANY_LOGO,
  sectionPadding: defaultPadding,
  maxHeight: 80,
};

export const companyAddressDefault: PoSection.CompanyAddress = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.COMPANY_ADDRESS,
  ...defaultFont,
  ...addressDefaults,
  sectionPadding: defaultPadding,
};

export const companyContactDefault: PoSection.CompanyContact = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.COMPANY_CONTACT,
  ...defaultFont,
  showPhone: true,
  showEmail: true,
  sectionPadding: defaultPadding,
};

// --- Supplier block defaults ---

export const supplierNameDefault: PoSection.SupplierName = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.SUPPLIER_NAME,
  ...defaultFont,
  fontSize: 14,
  fontWeight: PoFontWeight.BOLD,
  sectionPadding: defaultPadding,
};

export const supplierAddressDefault: PoSection.SupplierAddress = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.SUPPLIER_ADDRESS,
  ...defaultFont,
  ...addressDefaults,
  showPhone: true,
  showEmail: true,
  sectionPadding: defaultPadding,
};

export const supplierContactDefault: PoSection.SupplierContact = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.SUPPLIER_CONTACT,
  ...defaultFont,
  showPhone: true,
  showEmail: true,
  sectionPadding: defaultPadding,
};

// --- PO header block defaults ---

export const poHeaderDefault: PoSection.PoHeader = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.PO_HEADER,
  ...defaultFont,
  fontSize: 12,
  textAlign: "right",
  sectionPadding: defaultPadding,
};

export const poNumberDefault: PoSection.PoNumber = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.PO_NUMBER,
  ...defaultFont,
  fontSize: 18,
  fontWeight: PoFontWeight.BOLD,
  labelPrefix: "#",
  sectionPadding: defaultPadding,
};

export const creationDateDefault: PoSection.CreationDate = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.CREATION_DATE,
  ...defaultFont,
  sectionPadding: defaultPadding,
};

export const expectedDeliveryDateDefault: PoSection.ExpectedDeliveryDate = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.EXPECTED_DELIVERY_DATE,
  ...defaultFont,
  sectionPadding: defaultPadding,
};

export const paymentTermsDefault: PoSection.PaymentTerms = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.PAYMENT_TERMS,
  ...defaultFont,
  sectionPadding: defaultPadding,
};

export const shipToAddressDefault: PoSection.ShipToAddress = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.SHIP_TO_ADDRESS,
  ...defaultFont,
  ...addressDefaults,
  showHeader: true,
  header: "Ship To",
  sectionPadding: defaultPadding,
};

// --- Line items default ---

const defaultEnabledColumns: PoLineItemColumnType[] = [
  PoLineItemColumnType.SKU,
  PoLineItemColumnType.PRODUCT_NAME,
  PoLineItemColumnType.QUANTITY,
  PoLineItemColumnType.UNIT_COST,
  PoLineItemColumnType.TOTAL_COST,
];

export const lineItemsTableDefault: PoSection.LineItemsTable = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.LINE_ITEMS_TABLE,
  ...defaultFont,
  tableColumns: Object.values(PoLineItemColumnType).map((type) => ({
    type,
    enabled: defaultEnabledColumns.includes(type),
  })),
  includeHeader: true,
  headerBackgroundColor: "#f0f0f0",
  showSectionHeader: true,
  sectionHeader: "ITEMS",
  rowHeight: 28,
  tableBorderColor: "#c0c0c0",
  tableBorderWidth: 1,
  sectionPadding: defaultPadding,
};

// --- Shipments table default ---

const defaultEnabledShipmentColumns: PoShipmentColumnType[] = [
  PoShipmentColumnType.SHIPMENT_ID,
  PoShipmentColumnType.ESTIMATED_ARRIVAL,
  PoShipmentColumnType.DESTINATION,
  PoShipmentColumnType.TRACKING_NUMBER,
];

export const shipmentsTableDefault: PoSection.ShipmentsTable = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.SHIPMENTS_TABLE,
  ...defaultFont,
  tableColumns: Object.values(PoShipmentColumnType).map((type) => ({
    type,
    enabled: defaultEnabledShipmentColumns.includes(type),
  })),
  includeHeader: true,
  rowHeight: 36,
  tableBorderColor: "#c0c0c0",
  tableBorderWidth: 1,
  sectionPadding: defaultPadding,
};

// --- Summary default ---

export const poSummaryDefault: PoSection.PoSummary = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.PO_SUMMARY,
  ...defaultFont,
  showSubtotal: true,
  showTax: true,
  showShipping: true,
  showTotal: true,
  showCurrency: true,
  sectionPadding: defaultPadding,
};

// --- Notes default ---

export const notesFieldDefault: PoSection.NotesField = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.NOTES_FIELD,
  ...defaultFont,
  showHeader: true,
  header: "INSTRUCTIONS",
  sectionPadding: defaultPadding,
};

// --- Generic block defaults ---

export const textDefault: PoSection.Text = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.TEXT,
  sectionPadding: defaultPadding,
  sectionColor: "#ffffff",
  text: "",
  linkColor: "#1E90FF",
  textColor: "#000000",
  fontSize: 12,
  fontFamily: FontFamily.ARIAL,
};

export const imageDefault: PoSection.Image = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.IMAGE,
  imageUrl: "",
  sectionPadding: noPadding,
  sectionColor: "#ffffff",
  showCaption: false,
  caption: "",
  altText: "",
  maxHeight: 100,
};

export const spacerDefault: PoSection.Spacer = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.SPACER,
  height: 24,
  sectionColor: "#ffffff",
  sectionPadding: { top: 24, right: 0, bottom: 0, left: 0 },
};

export const lineDefault: PoSection.Line = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.LINE,
  sectionColor: "#ffffff",
  color: "#e5e5e5",
  sectionPadding: { top: 8, right: 0, bottom: 8, left: 0 },
};

export const columnDefault: PoSection.Column = {
  blockId: new ObjectId().toString(),
  type: PoBlockType.COLUMN,
  columns: [],
  columnCount: 2,
  sectionColor: "#ffffff",
  sectionPadding: noPadding,
  gap: 0,
};

// --- Default template layout matching current PO PDF ---

export const pageLayoutDefault: PoTemplatePageLayout = {
  orientation: PageOrientation.PORTRAIT,
  size: PageSize.FULL_SIZE,
};

export const defaultPoSections: PoSectionType[] = [
  // Row 1: Company Logo (left) | empty | empty | PO Header (right)
  {
    ...columnDefault,
    blockId: new ObjectId().toString(),
    columnCount: 4,
    gap: 0,
    columns: [
      { ...companyLogoDefault, blockId: new ObjectId().toString() },
      null,
      null,
      { ...poHeaderDefault, blockId: new ObjectId().toString() },
    ],
  },
  // Divider
  { ...lineDefault, blockId: new ObjectId().toString(), color: "#e5e5e5" },
  // Row 3: Supplier Address | Ship To Address
  {
    ...columnDefault,
    blockId: new ObjectId().toString(),
    columnCount: 2,
    gap: 30,
    columns: [
      {
        ...supplierAddressDefault,
        blockId: new ObjectId().toString(),
        showHeader: true,
        header: "SUPPLIER",
        showName: true,
        showPhone: true,
        showEmail: true,
      },
      {
        ...shipToAddressDefault,
        blockId: new ObjectId().toString(),
        showHeader: true,
        header: "BUYER",
        showName: true,
        showPhone: false,
        showEmail: false,
      },
    ],
  },
  // Spacer before tables
  {
    ...spacerDefault,
    blockId: new ObjectId().toString(),
    height: 16,
    sectionPadding: { top: 8, right: 0, bottom: 0, left: 0 },
  },
  // Shipments table
  { ...shipmentsTableDefault, blockId: new ObjectId().toString() },
  // Line items table
  { ...lineItemsTableDefault, blockId: new ObjectId().toString() },
  // Summary (total only)
  {
    ...poSummaryDefault,
    blockId: new ObjectId().toString(),
    showSubtotal: false,
    showTax: false,
    showShipping: false,
    showTotal: true,
    showCurrency: true,
  },
];

export const defaultPoTemplate: Omit<
  PoTemplate,
  "_id" | "name" | "accountId" | "updatedAt"
> = { sections: defaultPoSections, pageLayout: pageLayoutDefault };
